/**
 * AgentsFlow Service
 * API integration layer for the AgentsFlow module
 *
 * Base URL: /api/v1/agents_flow/
 */

import { api } from '../../../api/Middleware';
import { API_URL } from '../../../api/Urls';

const AGENTS_FLOW_BASE = `${API_URL}agents_flow/`;

/**
 * AgentsFlow API Service
 */
const AgentsFlowService = {
  // ===============================================
  // AGENTS CRUD
  // ===============================================

  /**
   * List all agents
   * @param {Object} params - Query parameters (page, page_size, search, etc.)
   * @returns {Promise} API response with agents list
   */
  listAgents: async (params = {}) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/`, { params });
  },

  /**
   * Get single agent by ID
   * @param {string|number} id - Agent ID
   * @returns {Promise} API response with agent data
   */
  getAgent: async (id) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${id}/`);
  },

  /**
   * Create new agent
   * @param {Object} data - Agent data
   * @returns {Promise} API response with created agent
   */
  createAgent: async (data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/`, data);
  },

  /**
   * Quick create agent (Level 1 - Simplified)
   * @param {Object} data - Quick creation data
   * @returns {Promise} API response with created agent
   */
  quickCreateAgent: async (data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/quick_create/`, data);
  },

  /**
   * Update agent
   * @param {string|number} id - Agent ID
   * @param {Object} data - Updated agent data
   * @returns {Promise} API response with updated agent
   */
  updateAgent: async (id, data) => {
    return api.patch(`${AGENTS_FLOW_BASE}agents/${id}/`, data);
  },

  /**
   * Delete agent
   * @param {string|number} id - Agent ID
   * @returns {Promise} API response
   */
  deleteAgent: async (id) => {
    return api.delete(`${AGENTS_FLOW_BASE}agents/${id}/`);
  },

  /**
   * Duplicate an existing agent
   * @param {string|number} id - Agent ID to duplicate
   * @returns {Promise} API response with duplicated agent
   */
  duplicateAgent: async (id) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${id}/duplicate/`);
  },

  /**
   * Publish/Activate agent
   * @param {string|number} id - Agent ID
   * @returns {Promise} API response
   */
  publishAgent: async (id) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${id}/publish/`);
  },

  /**
   * Migrate embeddings (destructive — wipes knowledge base).
   * Apaga todos os chunks indexados e atualiza embedding_provider/model.
   * @param {string|number} id - Agent ID
   * @param {Object} payload - { embedding_provider, embedding_model, embedding_vault_key_id?, confirm: true }
   * @returns {Promise} API response
   */
  migrateEmbeddings: async (id, payload) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${id}/embeddings/migrate/`, payload);
  },

  /**
   * Test API call with mock data (Smart Body)
   * @param {string|number} id - Agent ID
   * @param {Object} data - API call config { endpoint, method, headers, bodyTemplate, bodyFormatterModel, timeout }
   * @returns {Promise} API response with test results
   */
  testApiCall: async (id, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${id}/test_api_call/`, data);
  },

  /**
   * Test agent with message
   * @param {string|number} id - Agent ID
   * @param {Object} data - Test message data { message: string, context?: object }
   * @returns {Promise} API response with agent reply
   */
  testAgent: async (id, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${id}/test_message/`, data);
  },

  /**
   * Test agent with media (image/audio)
   * @param {string|number} id - Agent ID
   * @param {FormData} formData - Form data with media file
   * @returns {Promise} API response with agent reply
   */
  testAgentMedia: async (id, formData) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${id}/test_message/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * Copilot — assistente de debug
   * @param {string|number} id - Agent ID
   * @param {Object} data - { message: string, context?: object }
   * @returns {Promise} API response with copilot reply
   */
  copilot: async (id, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${id}/copilot/`, data);
  },

  /**
   * Analisa e melhora a descrição de uma rota do intent_router.
   *
   * Considera todas as rotas do mesmo router para detectar overlap.
   * Retorna score, issues, descrição sugerida, exemplos que casam/não casam.
   *
   * @param {string} id - Agent ID
   * @param {Object} data - {
   *   router_node_id: string,
   *   intent_id: string,
   *   all_router_intents: Array<{id, key, label, description, type}>
   * }
   * @returns {Promise} API response with analysis
   */
  analyzeIntentDescription: async (id, data) => {
    return api.post(
      `${AGENTS_FLOW_BASE}agents/${id}/analyze_intent_description/`,
      data,
    );
  },

  /**
   * Analisa as instruções de um nó ai_response e sugere melhoria.
   *
   * @param {string} id - Agent ID
   * @param {Object} data - { node_id: string, current_instructions: string }
   * @returns {Promise} API response with analysis
   */
  analyzeAiResponseInstructions: async (id, data) => {
    return api.post(
      `${AGENTS_FLOW_BASE}agents/${id}/analyze_ai_response/`,
      data,
    );
  },

  // ===============================================
  // CAPABILITIES
  // ===============================================

  /**
   * List available capability definitions
   * @returns {Promise} API response with capabilities catalog
   */
  listCapabilities: async () => {
    return api.get(`${AGENTS_FLOW_BASE}capabilities/`);
  },

  /**
   * Get agent capabilities
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with agent's active capabilities
   */
  getAgentCapabilities: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/capabilities/`);
  },

  /**
   * Toggle multiple capabilities for an agent
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - { capabilities: [{ code: string, is_active: boolean }] }
   * @returns {Promise} API response
   */
  toggleCapabilities: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/capabilities/toggle/`, data);
  },

  /**
   * Update capability configuration
   * @param {string|number} agentId - Agent ID
   * @param {string} capabilityCode - Capability code (scheduling, faq, etc.)
   * @param {Object} config - Capability configuration
   * @returns {Promise} API response
   */
  updateCapabilityConfig: async (agentId, capabilityCode, config) => {
    return api.patch(
      `${AGENTS_FLOW_BASE}agents/${agentId}/capabilities/${capabilityCode}/`,
      config
    );
  },

  // ===============================================
  // KNOWLEDGE BASE (RAG)
  // ===============================================

  /**
   * Get agent knowledge base
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with knowledge sources
   */
  getKnowledge: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/knowledge/`);
  },

  /**
   * Upload document to knowledge base
   * @param {string|number} agentId - Agent ID
   * @param {FormData} formData - Form data with file
   * @returns {Promise} API response
   */
  uploadKnowledgeDocument: async (agentId, formData) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/knowledge/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * Delete knowledge source
   * @param {string|number} agentId - Agent ID
   * @param {string|number} knowledgeId - Knowledge source ID
   * @returns {Promise} API response
   */
  deleteKnowledge: async (agentId, knowledgeId) => {
    return api.delete(`${AGENTS_FLOW_BASE}agents/${agentId}/knowledge/${knowledgeId}/`);
  },

  /**
   * Sync external knowledge source
   * @param {string|number} agentId - Agent ID
   * @param {string|number} knowledgeId - Knowledge source ID
   * @returns {Promise} API response
   */
  syncKnowledge: async (agentId, knowledgeId) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/knowledge/${knowledgeId}/sync/`);
  },

  /**
   * Lista tags únicas usadas pelos documentos do agente (para autocomplete).
   * @param {string|number} agentId - Agent ID
   * @returns {Promise<string[]>} Lista de tags
   */
  getKnowledgeCategories: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/knowledge-categories/`);
  },

  /**
   * Atualiza tags de um documento (sem re-upload).
   * @param {string|number} agentId - Agent ID
   * @param {string|number} knowledgeId - Knowledge source ID
   * @param {string[]} categories - Lista de tags (max 1 no frontend)
   */
  updateKnowledgeCategories: async (agentId, knowledgeId, categories) => {
    return api.patch(
      `${AGENTS_FLOW_BASE}agents/${agentId}/knowledge/${knowledgeId}/categories/`,
      { categories },
    );
  },

  /**
   * Connect Google Sheets
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - { spreadsheet_id: string, sheet_name?: string }
   * @returns {Promise} API response
   */
  connectGoogleSheets: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/knowledge/google_sheets/`, data);
  },

  /**
   * Add website for scraping
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - { url, sync_frequency, max_depth, max_pages, ... }
   * @returns {Promise} API response
   */
  addWebsiteKnowledge: async (agentId, data) => {
    const payload = {
      source_type: 'website',
      external_url: data.url,
      sync_frequency: data.sync_frequency || 'manual',
      external_config: {
        max_depth: 1,
        max_pages: Math.min(data.max_pages ?? 10, 25),
        include_patterns: data.include_patterns || '',
        exclude_patterns: data.exclude_patterns || '',
      },
    };
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/knowledge/`, payload);
  },

  /**
   * Connect Notion
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - { database_id?: string, page_id?: string, sync_frequency?: string }
   * @returns {Promise} API response
   */
  connectNotion: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/knowledge/notion/`, data);
  },

  /**
   * Connect Google Drive folder
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - { folder_id: string, sync_frequency?: string, file_types?: string[] }
   * @returns {Promise} API response
   */
  connectGoogleDrive: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/knowledge/`, data);
  },

  // ===============================================
  // MEDIA LIBRARY
  // ===============================================

  /**
   * List media from agent's gallery
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with media list
   */
  listMedia: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/media/`);
  },

  /**
   * Upload media to agent's gallery
   * @param {string|number} agentId - Agent ID
   * @param {FormData} formData - Form data with file and name
   * @returns {Promise} API response with created media
   */
  uploadMedia: async (agentId, formData) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/media/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * Update media name/description
   * @param {string|number} agentId - Agent ID
   * @param {string} mediaId - Media ID
   * @param {Object} data - { name?, description? }
   * @returns {Promise} API response with updated media
   */
  updateMedia: async (agentId, mediaId, data) => {
    return api.patch(`${AGENTS_FLOW_BASE}agents/${agentId}/media/${mediaId}/`, data);
  },

  /**
   * Delete media from agent's gallery
   * @param {string|number} agentId - Agent ID
   * @param {string} mediaId - Media ID
   * @returns {Promise} API response
   */
  deleteMedia: async (agentId, mediaId) => {
    return api.delete(`${AGENTS_FLOW_BASE}agents/${agentId}/media/${mediaId}/`);
  },

  /**
   * Transcribe audio file using the agent's configured LLM provider
   * @param {string|number} agentId - Agent ID
   * @param {File|Blob} file - Audio file to transcribe
   * @returns {Promise} API response with { transcription: string }
   */
  transcribeAudio: async (agentId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/transcribe_audio/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // ===============================================
  // INTEGRATIONS
  // ===============================================

  /**
   * Get agent integrations
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with integrations
   */
  getIntegrations: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/integrations/`);
  },

  /**
   * Connect integration
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - Integration connection data
   * @returns {Promise} API response
   */
  connectIntegration: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/integrations/`, data);
  },

  /**
   * Update integration configuration
   * @param {string|number} agentId - Agent ID
   * @param {string|number} integrationId - Integration ID
   * @param {Object} config - Updated configuration
   * @returns {Promise} API response
   */
  updateIntegration: async (agentId, integrationId, config) => {
    return api.patch(
      `${AGENTS_FLOW_BASE}agents/${agentId}/integrations/${integrationId}/`,
      config
    );
  },

  /**
   * Disconnect integration
   * @param {string|number} agentId - Agent ID
   * @param {string|number} integrationId - Integration ID
   * @returns {Promise} API response
   */
  disconnectIntegration: async (agentId, integrationId) => {
    return api.delete(`${AGENTS_FLOW_BASE}agents/${agentId}/integrations/${integrationId}/`);
  },

  /**
   * Test integration connection
   * @param {string|number} agentId - Agent ID
   * @param {string|number} integrationId - Integration ID
   * @returns {Promise} API response with test result
   */
  testIntegration: async (agentId, integrationId) => {
    return api.post(
      `${AGENTS_FLOW_BASE}agents/${agentId}/integrations/${integrationId}/test/`
    );
  },

  /**
   * Revoke all Google access for an agent
   * Revokes OAuth token at Google and removes all Google integrations
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with revocation result
   */
  revokeGoogleAccess: async (agentId) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/revoke-google-access/`);
  },

  // ===============================================
  // NOTIFICATION RULES
  // ===============================================

  getNotificationRules: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/notification_rules/`);
  },

  createNotificationRule: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/notification_rules/`, data);
  },

  updateNotificationRule: async (agentId, ruleId, data) => {
    return api.put(`${AGENTS_FLOW_BASE}agents/${agentId}/notification_rules/${ruleId}/`, data);
  },

  deleteNotificationRule: async (agentId, ruleId) => {
    return api.delete(`${AGENTS_FLOW_BASE}agents/${agentId}/notification_rules/${ruleId}/`);
  },

  // ===============================================
  // DAILY SUMMARY
  // ===============================================

  getDailySummaryConfig: async () => {
    return api.get(`${AGENTS_FLOW_BASE}agents/daily_summary_config/`);
  },

  updateDailySummaryConfig: async (data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/daily_summary_config/`, data);
  },

  getAvailableSummaryModules: async () => {
    return api.get(`${AGENTS_FLOW_BASE}agents/available_summary_modules/`);
  },

  // ===============================================
  // VISUAL GRAPH (Level 3)
  // ===============================================

  /**
   * Get agent visual graph
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with graph definition
   */
  getGraph: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/graph/`);
  },

  /**
   * Update agent visual graph
   * @param {string|number} agentId - Agent ID
   * @param {Object} graphDefinition - Graph definition JSON
   * @returns {Promise} API response
   */
  updateGraph: async (agentId, graphDefinition) => {
    return api.put(`${AGENTS_FLOW_BASE}agents/${agentId}/graph/`, {
      graph_definition: graphDefinition
    });
  },

  // ===============================================
  // TEMPLATES
  // ===============================================

  /**
   * List available templates
   * @param {Object} params - Query parameters (sector, etc.)
   * @returns {Promise} API response with templates
   */
  listTemplates: async (params = {}) => {
    return api.get(`${AGENTS_FLOW_BASE}templates/`, { params });
  },

  /**
   * Get template by ID
   * @param {string|number} id - Template ID
   * @returns {Promise} API response with template data
   */
  getTemplate: async (id) => {
    return api.get(`${AGENTS_FLOW_BASE}templates/${id}/`);
  },

  /**
   * Create agent from template
   * @param {string|number} templateId - Template ID
   * @param {Object} customizations - Customization overrides
   * @returns {Promise} API response with created agent
   */
  createFromTemplate: async (templateId, customizations = {}) => {
    return api.post(`${AGENTS_FLOW_BASE}templates/${templateId}/create_agent/`, customizations);
  },

  // ===============================================
  // ANALYTICS & METRICS
  // ===============================================

  /**
   * Get agent analytics
   * @param {string|number} agentId - Agent ID
   * @param {Object} params - Date range and other filters
   * @returns {Promise} API response with analytics data
   */
  getAnalytics: async (agentId, params = {}) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/analytics/`, { params });
  },

  /**
   * Get improvement suggestions
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with suggestions
   */
  getSuggestions: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/suggestions/`);
  },

  // ===============================================
  // MESSAGES / CUSTOM TEXTS
  // ===============================================

  /**
   * Get agent custom messages
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with messages
   */
  getMessages: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/messages/`);
  },

  /**
   * Update agent custom messages
   * @param {string|number} agentId - Agent ID
   * @param {Object} messages - Custom messages object
   * @returns {Promise} API response
   */
  updateMessages: async (agentId, messages) => {
    return api.put(`${AGENTS_FLOW_BASE}agents/${agentId}/messages/`, messages);
  },

  // ===============================================
  // FAQ
  // ===============================================

  /**
   * Get agent FAQ entries
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with FAQ list
   */
  getFAQ: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/faq/`);
  },

  /**
   * Add FAQ entry
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - { question: string, answer: string }
   * @returns {Promise} API response
   */
  addFAQEntry: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/faq/`, data);
  },

  /**
   * Update FAQ entry
   * @param {string|number} agentId - Agent ID
   * @param {string|number} faqId - FAQ entry ID
   * @param {Object} data - Updated FAQ data
   * @returns {Promise} API response
   */
  updateFAQEntry: async (agentId, faqId, data) => {
    return api.patch(`${AGENTS_FLOW_BASE}agents/${agentId}/faq/${faqId}/`, data);
  },

  /**
   * Delete FAQ entry
   * @param {string|number} agentId - Agent ID
   * @param {string|number} faqId - FAQ entry ID
   * @returns {Promise} API response
   */
  deleteFAQEntry: async (agentId, faqId) => {
    return api.delete(`${AGENTS_FLOW_BASE}agents/${agentId}/faq/${faqId}/`);
  },

  // ===============================================
  // LLM CONFIGURATION
  // ===============================================

  /**
   * Get agent LLM configuration
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with LLM config
   */
  getLLMConfig: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/llm_config/`);
  },

  /**
   * Update agent LLM configuration
   * @param {string|number} agentId - Agent ID
   * @param {Object} config - LLM configuration (provider, model, max_tokens, etc.)
   * @returns {Promise} API response
   */
  updateLLMConfig: async (agentId, config) => {
    return api.put(`${AGENTS_FLOW_BASE}agents/${agentId}/llm_config/`, config);
  },

  // ===============================================
  // ADVANCED CONFIGURATION
  // ===============================================

  /**
   * Get agent advanced configuration
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with advanced config
   */
  getAdvancedConfig: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/advanced_config/`);
  },

  /**
   * Update agent advanced configuration
   * @param {string|number} agentId - Agent ID
   * @param {Object} config - Advanced configuration (max_iterations, hitl, memory, etc.)
   * @returns {Promise} API response
   */
  updateAdvancedConfig: async (agentId, config) => {
    return api.put(`${AGENTS_FLOW_BASE}agents/${agentId}/advanced_config/`, config);
  },

  // ===============================================
  // PRODUCTS (for sales agents)
  // ===============================================

  /**
   * Get agent products catalog
   * @param {string|number} agentId - Agent ID
   * @param {Object} params - Query parameters (page, search, category, etc.)
   * @returns {Promise} API response with products list
   */
  getProducts: async (agentId, params = {}) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/products/`, { params });
  },

  /**
   * Add product to catalog
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - Product data
   * @returns {Promise} API response
   */
  addProduct: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/products/`, data);
  },

  /**
   * Update product
   * @param {string|number} agentId - Agent ID
   * @param {string|number} productId - Product ID
   * @param {Object} data - Updated product data
   * @returns {Promise} API response
   */
  updateProduct: async (agentId, productId, data) => {
    return api.patch(`${AGENTS_FLOW_BASE}agents/${agentId}/products/${productId}/`, data);
  },

  /**
   * Delete product
   * @param {string|number} agentId - Agent ID
   * @param {string|number} productId - Product ID
   * @returns {Promise} API response
   */
  deleteProduct: async (agentId, productId) => {
    return api.delete(`${AGENTS_FLOW_BASE}agents/${agentId}/products/${productId}/`);
  },

  /**
   * Import products from file
   * @param {string|number} agentId - Agent ID
   * @param {FormData} formData - Form data with file
   * @returns {Promise} API response
   */
  importProducts: async (agentId, formData) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/products/import/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  /**
   * Import products from Google Sheets
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - { spreadsheet_id: string, sheet_name?: string }
   * @returns {Promise} API response
   */
  importProductsFromSheets: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/products/import_sheets/`, data);
  },

  /**
   * Sync products from external source
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response
   */
  syncProducts: async (agentId) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/products/sync/`);
  },

  // ===============================================
  // TRAINING (RAG Processing)
  // ===============================================

  /**
   * Start agent training (RAG processing)
   * Processes all knowledge sources and creates embeddings
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with training status and task IDs
   */
  trainAgent: async (agentId, knowledgeIds = null) => {
    const data = knowledgeIds ? { knowledge_ids: knowledgeIds } : {};
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/train/`, data);
  },

  /**
   * Get training status
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with current training status
   */
  getTrainingStatus: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/train/status/`);
  },

  // ===============================================
  // MEMORY MANAGEMENT
  // ===============================================

  /**
   * Reset agent memory and conversation context
   * Clears all checkpoints and memory for the agent
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with reset status
   */
  resetMemory: async (agentId) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/reset-memory/`);
  },

  // ===============================================
  // PLAYGROUND TEST SESSIONS
  // ===============================================

  /**
   * Lista sessões de teste salvas do playground.
   * Retorna: { sessions: [{id, name, preview, message_count, created_at}], count, limit }
   */
  listPlaygroundSessions: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/playground-sessions/`);
  },

  /**
   * Arquiva a sessão atual.
   * Body: { messages: [{id, type, content, timestamp, metadata?}], name?: string }
   * 409 se limite (10) atingido — frontend deve abrir UI de gerenciamento.
   */
  archivePlaygroundSession: async (agentId, payload) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/playground-sessions/`, payload);
  },

  /**
   * Carrega detalhe de uma sessão (com messages) para replay.
   */
  getPlaygroundSession: async (agentId, sessionId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/playground-sessions/${sessionId}/`);
  },

  /**
   * Apaga 1 sessão salva.
   */
  deletePlaygroundSession: async (agentId, sessionId) => {
    return api.delete(`${AGENTS_FLOW_BASE}agents/${agentId}/playground-sessions/${sessionId}/`);
  },

  /**
   * Apaga TODAS as sessões salvas deste agente.
   */
  clearAllPlaygroundSessions: async (agentId) => {
    return api.delete(`${AGENTS_FLOW_BASE}agents/${agentId}/playground-sessions/`);
  },

  // ===============================================
  // HUMANIZATION
  // ===============================================

  /**
   * Preview humanization on a text
   * Used for testing how text will be humanized before saving
   * @param {string|number} agentId - Agent ID
   * @param {Object} data - { text: string, config?: object }
   * @returns {Promise} API response with humanized result
   */
  previewHumanization: async (agentId, data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/humanization/preview/`, data);
  },

  // ===============================================
  // GOOGLE WORKSPACE OAUTH
  // ===============================================

  /**
   * Get Google OAuth authorization URL
   * @param {string|number} agentId - Agent ID
   * @param {string} redirectUri - OAuth redirect URI
   * @param {string[]} services - Google services to request (calendar, drive, gmail, docs)
   * @returns {Promise} API response with auth_url
   */
  getGoogleAuthUrl: async (agentId, redirectUri, services = ['calendar', 'drive', 'gmail', 'docs']) => {
    return api.get(`${AGENTS_FLOW_BASE}google/authorize/`, {
      params: {
        agent_flow_id: agentId,
        redirect_uri: redirectUri,
        services: services.join(','),
      }
    });
  },

  /**
   * Check Google integration status
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with integration status
   */
  checkGoogleStatus: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}google/status/`, {
      params: { agent_flow_id: agentId }
    });
  },

  /**
   * Disconnect Google integration
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response
   */
  disconnectGoogle: async (agentId) => {
    return api.post(`${AGENTS_FLOW_BASE}google/disconnect/`, {
      agent_flow_id: agentId
    });
  },

  // ===============================================
  // WHATSAPP CONNECTION
  // ===============================================

  /**
   * Get WhatsApp connection status for agent
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response with WhatsApp status
   */
  getWhatsappStatus: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/whatsapp/status/`);
  },

  /**
   * Disconnect WhatsApp from agent (unlinks only, does not delete WAHA session)
   * @param {string|number} agentId - Agent ID
   * @returns {Promise} API response
   */
  disconnectWhatsapp: async (agentId) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/${agentId}/whatsapp/disconnect/`);
  },

  // ===============================================
  // REACTIVATION CONFIG (per-agent)
  // ===============================================

  getReactivationConfig: async (id) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${id}/reactivation/`);
  },

  updateReactivationConfig: async (id, data) => {
    return api.put(`${AGENTS_FLOW_BASE}agents/${id}/reactivation/`, data);
  },

  // ===============================================
  // API KEY VAULT
  // ===============================================

  getVaultKeys: async () => {
    return api.get(`${API_URL}account/settings/vault/`);
  },
  addVaultKey: async (data) => {
    return api.post(`${API_URL}account/settings/vault/add/`, data);
  },
  updateVaultKey: async (keyId, data) => {
    return api.patch(`${API_URL}account/settings/vault/${keyId}/`, data);
  },
  deleteVaultKey: async (keyId) => {
    return api.delete(`${API_URL}account/settings/vault/${keyId}/delete/`);
  },

  // ===============================================
  // WIZARD DE CRIAÇÃO (AI-ASSISTED)
  // ===============================================

  wizardIdealize: async (data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/wizard/idealize/`, data);
  },

  wizardPlan: async (data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/wizard/plan/`, data);
  },

  wizardAnalyzeKnowledge: async (formData) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/wizard/analyze-knowledge/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  wizardCorrectFAQ: async (data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/wizard/correct-faq/`, data);
  },

  wizardCreate: async (data) => {
    return api.post(`${AGENTS_FLOW_BASE}agents/wizard/create/`, data);
  },

  getWizardGuide: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/wizard-guide/`, {
      responseType: 'blob',
    });
  },

  // ===============================================
  // RAG GUIDE TEMPLATES
  // ===============================================

  listRAGGuideTemplates: async () => {
    return api.get(`${AGENTS_FLOW_BASE}agents/rag-guide-templates/`);
  },

  downloadRAGGuideTemplate: async (templateId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/rag-guide-templates/${templateId}/download/`, {
      responseType: 'blob',
    });
  },

  // ===============================================
  // AUTOMATIONS / SCHEDULED MESSAGES
  // ===============================================

  /**
   * List automations (reminders) configured in the agent graph.
   * @param {string} agentId - AgentFlow UUID
   * @returns {Promise} { count, pending_messages_count, results: [...] }
   */
  getAutomations: async (agentId) => {
    return api.get(`${AGENTS_FLOW_BASE}agents/${agentId}/automations/`);
  },

  /**
   * List scheduled messages for an agent, paginated.
   * @param {Object} params - { agent_flow_id, number_contains, page, page_size, status, pending_only }
   * @returns {Promise} paginated DRF response
   */
  listScheduledMessages: async (params = {}) => {
    return api.get(`${API_URL}scheduled-messages/`, { params });
  },

  /**
   * Delete a single scheduled message (only if status is pending/skipped).
   * @param {number} id
   * @returns {Promise}
   */
  deleteScheduledMessage: async (id) => {
    return api.delete(`${API_URL}scheduled-messages/${id}/`);
  },

  /**
   * Bulk delete scheduled messages by ids (only pending/skipped get deleted).
   * @param {number[]} ids
   * @returns {Promise} { status, deleted_count }
   */
  bulkDeleteScheduledMessages: async (ids) => {
    return api.delete(`${API_URL}scheduled-messages/bulk-delete/`, {
      data: { ids },
    });
  },
};

export default AgentsFlowService;
